import React, { useState } from "react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";
import { FaFilePdf } from "react-icons/fa";
import calculatorParameters from "../../../data/calculator_parameters.json";

export const AnalyticsPdfExport = ({ optimizationResults, userData }) => {
  const [exporting, setExporting] = useState(false);

  // Create truck info mapping similar to AnalyticsHighlights
  const getTruckInfo = () => {
    const truckInfo = {};
    calculatorParameters.ElectricTruckModelInfo.forEach(truck => {
      truckInfo[truck.id] = {
        id: truck.id,
        name: truck.title,
        brand: truck.brand,
        make: truck.make,
        image: truck.imgUrl.startsWith('/') ? `${window.location.origin}${truck.imgUrl}` : truck.imgUrl,
        link: truck.oemLink,
        price: truck.msrp,
        batteryCapacity: truck.batteryCap,
        range: truck.operationRange,
        class: truck.class,
      };
    });
    return truckInfo;
  };

  const exportToPdf = async () => {
    setExporting(true);
    
    try {
      console.log("Starting PDF export...");
      
      // Create new jsPDF instance with error handling
      let pdf;
      try {
        pdf = new jsPDF("p", "mm", "a4");
        console.log("PDF instance created successfully");
      } catch (err) {
        console.error("Error creating PDF instance:", err);
        throw new Error("Failed to initialize PDF document");
      }
      
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      let currentY = 20;
      
      // Add header with title
      pdf.setFontSize(22);
      pdf.setTextColor(44, 62, 80);
      pdf.text("Fleet Optimization Report", pageWidth / 2, currentY, { align: "center" });
      currentY += 10;
      
      // Check if data is available
      if (!optimizationResults?.data) {
        console.error("Optimization results data is missing:", optimizationResults);
        throw new Error("No optimization data available to export");
      }
      
      pdf.setFontSize(12);
      pdf.text(`Generated for: ${userData?.company_name || userData?.username || 'Your Company'}`, pageWidth / 2, currentY, { align: "center" });
      currentY += 6;
      pdf.text(`Date: ${new Date().toLocaleDateString()}`, pageWidth / 2, currentY, { align: "center" });
      currentY += 15;
      
      // Case information
      pdf.setFontSize(16);
      pdf.text("Case Information", 14, currentY);
      currentY += 8;
      pdf.setFontSize(12);
      pdf.text(`Case Name: ${optimizationResults?.case_name || 'Current Optimization'}`, 14, currentY);
      currentY += 15;

      // ----- EXECUTIVE SUMMARY SECTION -----
      pdf.setFontSize(16);
      pdf.text("Executive Summary", 14, currentY);
      currentY += 8;
      
      // Key highlights from AnalyticsHighlights
      const savings = optimizationResults?.data?.finance?.tco_analysis?.financial_metrics?.total_cost_of_ownership?.savings || 0;
      const tcoPerMileSavings = optimizationResults?.data?.finance?.tco_analysis?.financial_metrics?.tco_per_mile?.savings || 0;
      const currentPerMile = optimizationResults?.data?.finance?.tco_analysis?.financial_metrics?.tco_per_mile?.pure_diesel_fleet || 0;
      const optimizedPerMile = optimizationResults?.data?.finance?.tco_analysis?.financial_metrics?.tco_per_mile?.optimized_fleet || 0;
      
      const routes = optimizationResults?.data?.operation?.routes || {};
      const depots = optimizationResults?.data?.operation?.depots || {};
      const electricRoutesCount = Object.values(routes).filter(route => route?.is_electric === true).length;
      const totalRoutes = Object.keys(routes).length;
      const totalChargers = Object.values(depots).reduce((total, depot) => {
        return total + Object.values(depot?.chargers || {}).reduce((sum, num) => sum + num, 0);
      }, 0);
      
      const executiveSummary = [
        ["Total 10-Year Savings", `$${Math.round(savings).toLocaleString()}`],
        ["Cost per Mile Reduction", `$${tcoPerMileSavings.toFixed(3)}`],
        ["Routes Recommended for Electrification", `${electricRoutesCount} of ${totalRoutes}`],
        ["Total Chargers Required", totalChargers.toString()],
        ["Electrification Percentage", `${totalRoutes > 0 ? ((electricRoutesCount / totalRoutes) * 100).toFixed(1) : '0.0'}%`],
      ];
      
      try {
        autoTable(pdf, {
          startY: currentY,
          head: [["Key Metric", "Value"]],
          body: executiveSummary,
          theme: 'striped',
          headStyles: { fillColor: [44, 62, 80] },
        });
        currentY = pdf.lastAutoTable.finalY + 15;
      } catch (err) {
        console.error("Error creating executive summary table:", err);
        currentY += 50;
      }

      // ----- FINANCIAL HIGHLIGHTS SECTION -----
      pdf.setFontSize(16);
      pdf.text("Financial Highlights", 14, currentY);
      currentY += 8;
      
      // Access TCO and other financial metrics
      const tcoAnalysis = optimizationResults?.data?.finance?.tco_analysis || {};
      const financialMetrics = tcoAnalysis?.financial_metrics || {};
      
      const totalCostOptimized = financialMetrics?.total_cost_of_ownership?.optimized_fleet || 0;
      const totalCostDiesel = financialMetrics?.total_cost_of_ownership?.pure_diesel_fleet || 0;
      const paybackPeriod = financialMetrics?.payback_period || 0;
      const roi = financialMetrics?.roi || 0;
      const annualOperationalSavings = tcoAnalysis?.annual_operational_costs?.savings || 0;
      
      // Financial highlights with key metrics
      const financialHighlights = [
        ["Total 10-Year TCO (Optimized Fleet)", `$${Math.round(totalCostOptimized).toLocaleString()}`],
        ["Total 10-Year TCO (Pure Diesel)", `$${Math.round(totalCostDiesel).toLocaleString()}`],
        ["Total Cost Savings (10 years)", `$${Math.round(savings).toLocaleString()}`],
        ["Savings Percentage", `${totalCostDiesel > 0 ? ((savings / totalCostDiesel) * 100).toFixed(1) : '0.0'}%`],
        ["Annual Operational Savings", `$${Math.round(annualOperationalSavings).toLocaleString()}`],
        ["Payback Period", `${paybackPeriod.toFixed(1)} years`],
        ["Return on Investment", `${roi.toFixed(1)}%`],
        ["Optimized Cost per Mile", `$${optimizedPerMile.toFixed(3)}`],
        ["Current Cost per Mile", `$${currentPerMile.toFixed(3)}`],
        ["Cost per Mile Reduction", `$${tcoPerMileSavings.toFixed(3)}`],
      ];
      
      try {
        autoTable(pdf, {
          startY: currentY,
          head: [["Metric", "Value"]],
          body: financialHighlights,
          theme: 'striped',
          headStyles: { fillColor: [44, 62, 80] },
        });
        currentY = pdf.lastAutoTable.finalY + 15;
      } catch (err) {
        console.error("Error creating financial highlights table:", err);
        currentY += 80;
      }
      
      // ----- UPFRONT COSTS SECTION -----
      pdf.setFontSize(16);
      pdf.text("Upfront Costs Breakdown", 14, currentY);
      currentY += 8;
      
      // Get upfront costs with same structure as AnalyticsFinancials
      const dieselUpfrontCosts = optimizationResults?.data?.finance?.cost_overview?.upfront_costs?.diesel || {
        vehicle_cost: 0
      };
      
      const electricUpfrontCosts = optimizationResults?.data?.finance?.cost_overview?.upfront_costs?.electric || {
        vehicle_cost: 0,
        depot_construction: 0,
        charger_installation: 0,
        power_interconnection: 0,
        solar_installation: 0,
        battery_installation: 0
      };
      
      const upfrontCostsBreakdown = [
        ["Diesel Vehicles", `$${Math.round(dieselUpfrontCosts?.vehicle_cost).toLocaleString()}`],
        ["Electric Vehicles", `$${Math.round(electricUpfrontCosts?.vehicle_cost).toLocaleString()}`],
        ["Depot Construction", `$${Math.round(electricUpfrontCosts?.depot_construction).toLocaleString()}`],
        ["Charger Installation", `$${Math.round(electricUpfrontCosts?.charger_installation).toLocaleString()}`],
        ["Power Interconnection", `$${Math.round(electricUpfrontCosts?.power_interconnection).toLocaleString()}`],
        ["Solar Installation", `$${Math.round(electricUpfrontCosts?.solar_installation).toLocaleString()}`],
        ["Battery Storage Installation", `$${Math.round(electricUpfrontCosts?.battery_installation).toLocaleString()}`],
        ["Total Upfront Investment", `$${Math.round(optimizationResults?.data?.finance?.cost_overview?.upfront_costs?.total).toLocaleString()}`],
      ];
      
      try {
        autoTable(pdf, {
          startY: currentY,
          head: [["Upfront Cost Category", "Amount"]],
          body: upfrontCostsBreakdown,
          theme: 'striped',
          headStyles: { fillColor: [44, 62, 80] },
        });
        currentY = pdf.lastAutoTable.finalY + 15;
      } catch (err) {
        console.error("Error creating upfront costs table:", err);
        currentY += 60;
      }
      
      // ----- CUMULATIVE COSTS OVER TIME -----
      if (tcoAnalysis?.cumulative_costs?.years && tcoAnalysis?.cumulative_costs?.years.length > 0) {
        pdf.setFontSize(16);
        pdf.text("Cumulative Cost Over Time", 14, currentY);
        currentY += 8;
        
        const years = tcoAnalysis.cumulative_costs.years;
        const optimizedCosts = tcoAnalysis.cumulative_costs.optimized_fleet;
        const dieselCosts = tcoAnalysis.cumulative_costs.pure_diesel_fleet;
        
        const cumulativeCostsData = years.map((year, index) => {
          const dieselCost = dieselCosts[index] || 0;
          const optimizedCost = optimizedCosts[index] || 0;
          const difference = dieselCost - optimizedCost;
          const savingsPercent = dieselCost > 0 ? ((difference / dieselCost) * 100).toFixed(1) + '%' : '0.0%';
          
          return [
            `Year ${year}`,
            `$${Math.round(optimizedCost).toLocaleString()}`,
            `$${Math.round(dieselCost).toLocaleString()}`,
            `$${Math.round(difference).toLocaleString()}`,
            savingsPercent
          ];
        });
        
        try {
          autoTable(pdf, {
            startY: currentY,
            head: [["Year", "Optimized Fleet", "Pure Diesel Fleet", "Savings", "Savings %"]],
            body: cumulativeCostsData,
            theme: 'striped',
            headStyles: { fillColor: [44, 62, 80] },
          });
          currentY = pdf.lastAutoTable.finalY + 15;
          
          // Add note explaining Year 0 values
          pdf.setFontSize(10);
          pdf.setTextColor(80, 80, 80);
          pdf.text("* Year 0 values represent upfront costs less the present value of residual values of the assets.", 14, currentY);
          currentY += 8;
          pdf.setTextColor(44, 62, 80); // Reset text color
        } catch (err) {
          console.error("Error creating cumulative costs table:", err);
          currentY += 60;
        }
      }

      // ----- FLEET ELECTRIFICATION SUMMARY -----
      pdf.setFontSize(16);
      pdf.text("Fleet Electrification Summary", 14, currentY);
      currentY += 8;
      
      // Get charger counts from depots safely
      const totalDepots = Object.keys(depots).length;
      const utilizedStations = Object.values(optimizationResults?.data?.operation?.charging_stations || {}).filter(station => station.is_utilized);
      
      // Calculate total charging power
      const totalChargingPower = Object.values(depots).reduce((total, depot) => {
        return total + Object.entries(depot.chargers || {}).reduce((sum, [power, count]) => {
          return sum + (parseInt(power) * count);
        }, 0);
      }, 0);
      
      const electrificationSummary = [
        ["Total Routes", totalRoutes.toString()],
        ["Electrified Routes", electricRoutesCount.toString()],
        ["Percentage Electrified", `${totalRoutes > 0 ? ((electricRoutesCount / totalRoutes) * 100).toFixed(1) : '0.0'}%`],
        ["Total Depots", totalDepots.toString()],
        ["Total Chargers Required", totalChargers.toString()],
        ["Total Charging Power", `${totalChargingPower.toFixed(0)} kW`],
        ["Public Charging Stations Used", utilizedStations.length.toString()],
      ];
      
      try {
        autoTable(pdf, {
          startY: currentY,
          head: [["Electrification Metric", "Value"]],
          body: electrificationSummary,
          theme: 'striped',
          headStyles: { fillColor: [44, 62, 80] },
        });
        currentY = pdf.lastAutoTable.finalY + 15;
      } catch (err) {
        console.error("Error creating electrification summary table:", err);
        currentY += 50;
      }

      // ----- ROUTE DETAILS -----
      if (Object.keys(routes).length > 0) {
        pdf.setFontSize(16);
        pdf.text("Route Details", 14, currentY);
        currentY += 8;
        
        const truckInfo = getTruckInfo();
        const routeDetails = Object.values(routes).map(route => {
          const truckName = route.selected_model_id && truckInfo[route.selected_model_id] 
            ? truckInfo[route.selected_model_id].name 
            : route.selected_model_id || 'N/A';
          
          // Get battery capacity for electric trucks
          const batteryCapacity = route.is_electric && route.selected_model_id && truckInfo[route.selected_model_id]
            ? truckInfo[route.selected_model_id].batteryCapacity
            : null;
          
          // Calculate energy consumption and charging activities from trip itinerary if available
          let energyConsumption = null;
          let chargingActivities = 0;
          if (route.trip_itinerary && route.trip_itinerary.stops) {
            energyConsumption = route.trip_itinerary.stops.reduce((total, stop) => {
              return total + (stop.charging?.amount_charged_kwh || 0);
            }, 0);
            
            // Count charging activities (stops where charging occurred)
            chargingActivities = route.trip_itinerary.stops.filter(stop => 
              stop.charging?.is_charged === true
            ).length;
          }
          
          return [
            route.route_name || 'Unknown Route',
            route.is_electric ? 'Electric' : 'Diesel',
            truckName,
            batteryCapacity ? `${batteryCapacity} kWh` : 'N/A',
            energyConsumption ? `${energyConsumption.toFixed(1)} kWh` : 'N/A',
            chargingActivities > 0 ? chargingActivities.toString() : 'N/A'
          ];
        });
        
        try {
          autoTable(pdf, {
            startY: currentY,
            head: [["Route Name", "Type", "Truck Model", "Battery Capacity", "Energy Consumption", "Charging Activities"]],
            body: routeDetails,
            theme: 'striped',
            headStyles: { fillColor: [44, 62, 80] },
          });
          currentY = pdf.lastAutoTable.finalY + 15;
        } catch (err) {
          console.error("Error creating route details table:", err);
          currentY += 50;
        }
      }

      // ----- DEPOT DETAILS -----
      if (Object.keys(depots).length > 0) {
        pdf.setFontSize(16);
        pdf.text("Depot Details", 14, currentY);
        currentY += 8;
        
        const depotDetails = Object.values(depots).map(depot => {
          // Format charger information with power levels
          const totalChargers = Object.values(depot.chargers || {}).reduce((sum, num) => sum + num, 0);
          const chargerInfo = totalChargers === 0 ? 'Not recommended' :
            Object.entries(depot.chargers || {})
              .filter(([_, count]) => count > 0)
              .map(([power, count]) => `${count}x ${power}kW`)
              .join(', ');
          
          // Get solar capacity from the correct path
          const solarCapacity = depot.solar?.is_installed ? 
            `${depot.solar.capacity_kw?.toFixed(1)} kW` : 'Not recommended';
          
          // Get battery capacity from the correct path
          const batteryCapacity = depot.battery?.is_installed ? 
            `${depot.battery.capacity_kwh?.toFixed(1)} kWh` : 'Not recommended';
          
          return [
            depot.depot_name || 'Unknown Depot',
            depot.depot_address || 'N/A',
            chargerInfo,
            solarCapacity,
            batteryCapacity
          ];
        });
        
        try {
          autoTable(pdf, {
            startY: currentY,
            head: [["Depot Name", "Location", "Charger", "Solar PV", "Battery Storage"]],
            body: depotDetails,
            theme: 'striped',
            headStyles: { fillColor: [44, 62, 80] },
          });
          currentY = pdf.lastAutoTable.finalY + 15;
        } catch (err) {
          console.error("Error creating depot details table:", err);
          currentY += 50;
        }
      }

      // ----- OPERATIONAL RECOMMENDATIONS -----
      pdf.setFontSize(16);
      pdf.text("Operational Recommendations", 14, currentY);
      currentY += 8;
      
      const recommendations = [];
      
      if (electricRoutesCount > 0) {
        recommendations.push([
          "Route Electrification",
          `${electricRoutesCount} routes recommended for electrification`,
          "Reduces operational costs"
        ]);
      }
      
      if (totalChargers > 0) {
        recommendations.push([
          "Charging Infrastructure",
          `${totalChargers} chargers required across ${totalDepots} depots`,
          "Ensures adequate charging capacity for fleet operations"
        ]);
      }
      
      if (utilizedStations.length > 0) {
        recommendations.push([
          "Public Charging",
          `${utilizedStations.length} public stations will be utilized`,
          "Leverages existing infrastructure for route flexibility"
        ]);
      }
      
      if (savings > 0) {
        recommendations.push([
          "Financial Impact",
          `$${Math.round(savings).toLocaleString()} in 10-year TCO savings`,
          "Significant cost reduction through electrification"
        ]);
      }
      
      if (recommendations.length > 0) {
        try {
          autoTable(pdf, {
            startY: currentY,
            head: [["Category", "Recommendation", "Rationale"]],
            body: recommendations,
            theme: 'striped',
            headStyles: { fillColor: [44, 62, 80] },
          });
          currentY = pdf.lastAutoTable.finalY + 15;
        } catch (err) {
          console.error("Error creating recommendations table:", err);
          currentY += 50;
        }
      }

      // Add footnote about detailed recommendations
      pdf.setFontSize(10);
      pdf.setTextColor(100, 100, 100);
      pdf.text("* Please refer back to the webtool for detailed charging schedules and charging location recommendations.", 14, currentY);
      currentY += 10;

      // Add footer
      pdf.setFontSize(10);
      pdf.setTextColor(100, 100, 100);
      pdf.text("Report generated by Fleet Optimization Tool", pageWidth / 2, pageHeight - 10, { align: "center" });
      
      // Save and download the PDF with explicit error handling
      console.log("Saving PDF...");
      try {
        const fileName = `fleet-optimization-${new Date().toISOString().split('T')[0]}.pdf`;
        pdf.save(fileName);
        console.log("PDF saved successfully:", fileName);
      } catch (err) {
        console.error("Error saving PDF:", err);
        // Alternative download approach
        try {
          const blob = pdf.output('blob');
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `fleet-optimization-${new Date().toISOString().split('T')[0]}.pdf`;
          link.click();
          URL.revokeObjectURL(url);
          console.log("PDF downloaded via alternative method");
        } catch (altErr) {
          console.error("Alternative download method failed:", altErr);
          throw new Error("Failed to download PDF");
        }
      }
    } catch (error) {
      console.error("Error in PDF export process:", error);
      alert(`Failed to generate PDF: ${error.message}`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="pdf-export-container">
      <button 
        className="btn-primary inline-flex items-center gap-2 font-nunito"
        onClick={exportToPdf}
        disabled={exporting}
      >
        <FaFilePdf />
        {exporting ? 'Generating PDF...' : 'Export to PDF'}
      </button>
    </div>
  );
}; 